import { create } from 'zustand';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  QueryDocumentSnapshot,
  query,
  serverTimestamp,
  Unsubscribe,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { db, storage } from '@/lib/firebase';

const DEFAULT_PARTICIPANT_NAME = '不明なユーザー';
const MAX_BATCH_WRITES = 450;
const pendingReadMessageKeys = new Set<string>();

// Message type
export type MessageType = 'text' | 'image';

// Message data
export interface Message {
  id: string;
  senderId: string;
  content: string;
  type: MessageType;
  imageUrl?: string;
  sentAt: Date;
  readAt?: Date;
}

// Conversation data
export interface Conversation {
  id: string;
  participants: string[];
  lastMessage?: string;
  lastMessageAt?: Date;
  lastMessageBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Conversation with details for display
export interface ConversationWithDetails extends Conversation {
  participant: {
    id: string;
    displayName: string;
    iconUrl?: string;
  };
  unreadCount: number;
}

interface MessageState {
  // State
  conversations: ConversationWithDetails[];
  messages: Message[];
  currentConversation: ConversationWithDetails | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;

  // Subscriptions
  conversationsUnsubscribe: Unsubscribe | null;
  messagesUnsubscribe: Unsubscribe | null;

  // Actions - Conversations
  subscribeToConversations: (userId: string) => void;
  unsubscribeFromConversations: () => void;
  findOrCreateConversation: (userId: string, otherUserId: string) => Promise<string>;
  getConversation: (conversationId: string, currentUserId: string) => Promise<ConversationWithDetails | null>;

  // Actions - Messages
  subscribeToMessages: (conversationId: string) => void;
  unsubscribeFromMessages: () => void;
  sendMessage: (conversationId: string, senderId: string, content: string, type?: MessageType) => Promise<void>;
  sendImageMessage: (conversationId: string, senderId: string, file: File) => Promise<void>;
  markAsRead: (conversationId: string, messageIds: string[], readerId: string) => Promise<void>;

  // Common
  setCurrentConversation: (conversation: ConversationWithDetails | null) => void;
  clearError: () => void;
  reset: () => void;
}

function normalizeParticipants(rawParticipants: unknown): string[] {
  if (!Array.isArray(rawParticipants)) {
    return [];
  }

  return rawParticipants.filter((id): id is string => typeof id === 'string');
}

function toDate(value: unknown): Date {
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
}

function createDefaultParticipant(id = ''): ConversationWithDetails['participant'] {
  return {
    id,
    displayName: DEFAULT_PARTICIPANT_NAME,
    iconUrl: undefined,
  };
}

interface UserAccessState {
  role: 'donor' | 'donee' | null;
  status: string | null;
  identityVerified: boolean;
}

async function fetchUserAccessState(userId: string): Promise<UserAccessState> {
  if (!userId) {
    return { role: null, status: null, identityVerified: false };
  }

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) {
    return { role: null, status: null, identityVerified: false };
  }

  const data = userSnap.data() as { role?: unknown; status?: unknown; identityVerified?: unknown };
  return {
    role: data.role === 'donor' || data.role === 'donee' ? data.role : null,
    status: typeof data.status === 'string' ? data.status : null,
    identityVerified: data.identityVerified === true,
  };
}

function validateConversationRestriction(sender: UserAccessState, recipient: UserAccessState): string | null {
  // donee -> donor は donor が approved の場合のみメッセージ可能
  if (sender.role === 'donee' && recipient.role === 'donor' && recipient.status !== 'approved') {
    return 'このドナーは現在メッセージを受け付けていません';
  }

  return null;
}

async function fetchParticipant(userId: string): Promise<ConversationWithDetails['participant']> {
  if (!userId) {
    return createDefaultParticipant();
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      return createDefaultParticipant(userId);
    }

    const userData = userSnap.data() as { displayName?: unknown; iconUrl?: unknown };
    return {
      id: userId,
      displayName: typeof userData.displayName === 'string' && userData.displayName
        ? userData.displayName
        : DEFAULT_PARTICIPANT_NAME,
      iconUrl: typeof userData.iconUrl === 'string' ? userData.iconUrl : undefined,
    };
  } catch (error) {
    console.warn('Could not fetch user info:', error);
    return createDefaultParticipant(userId);
  }
}

function getUnreadCount(data: Record<string, unknown>, userId: string): number {
  const unreadBy = data.unreadBy;
  if (typeof unreadBy !== 'object' || unreadBy === null) {
    return 0;
  }

  const unreadCount = (unreadBy as Record<string, unknown>)[userId];
  if (typeof unreadCount !== 'number') {
    return 0;
  }

  return Math.max(0, unreadCount);
}

export const useMessageStore = create<MessageState>((set, get) => {
  const resolveRecipientIds = async (conversationId: string, senderId: string): Promise<string[]> => {
    const currentConversation = get().currentConversation;
    if (currentConversation?.id === conversationId) {
      return currentConversation.participants.filter((id) => id !== senderId);
    }

    const conversationFromList = get().conversations.find((item) => item.id === conversationId);
    if (conversationFromList) {
      return conversationFromList.participants.filter((id) => id !== senderId);
    }

    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      if (!conversationSnap.exists()) {
        return [];
      }

      const data = conversationSnap.data() as { participants?: unknown };
      const participants = normalizeParticipants(data.participants);
      return participants.filter((id) => id !== senderId);
    } catch (error) {
      console.warn('Could not resolve recipient IDs:', error);
      return [];
    }
  };

  return {
    conversations: [],
    messages: [],
    currentConversation: null,
    isLoading: false,
    isSending: false,
    error: null,
    conversationsUnsubscribe: null,
    messagesUnsubscribe: null,

    // Subscribe to conversations list (real-time)
    subscribeToConversations: (userId: string) => {
      // Unsubscribe from previous subscription
      const prevUnsubscribe = get().conversationsUnsubscribe;
      if (prevUnsubscribe) {
        prevUnsubscribe();
      }

      set({ isLoading: true, error: null });

      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId),
        orderBy('lastMessageAt', 'desc'),
        limit(50)
      );

      const userCache = new Map<string, ConversationWithDetails['participant']>();
      let isActive = true;
      let latestSnapshotId = 0;

      const buildConversations = (snapshotDocs: QueryDocumentSnapshot[]): ConversationWithDetails[] => {
        return snapshotDocs.map((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const participants = normalizeParticipants(data.participants);
          const otherUserId = participants.find((id) => id !== userId) || '';
          const participant = otherUserId
            ? userCache.get(otherUserId) || createDefaultParticipant(otherUserId)
            : createDefaultParticipant();

          return {
            id: docSnap.id,
            participants,
            lastMessage: typeof data.lastMessage === 'string' ? data.lastMessage : undefined,
            lastMessageAt: toDate(data.lastMessageAt),
            lastMessageBy: typeof data.lastMessageBy === 'string' ? data.lastMessageBy : undefined,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            participant,
            unreadCount: getUnreadCount(data, userId),
          };
        });
      };

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const currentSnapshotId = ++latestSnapshotId;
          const docs = snapshot.docs;
          const otherUserIds = Array.from(new Set(
            docs
              .map((docSnap) => {
                const data = docSnap.data() as { participants?: unknown };
                const participants = normalizeParticipants(data.participants);
                return participants.find((id) => id !== userId) || '';
              })
              .filter((id) => id.length > 0)
          ));
          const missingUserIds = otherUserIds.filter((id) => !userCache.has(id));

          if (missingUserIds.length === 0) {
            set({ conversations: buildConversations(docs), isLoading: false });
            return;
          }

          // Render quickly with placeholder participants, then hydrate names/icons.
          set({ conversations: buildConversations(docs), isLoading: false });

          void (async () => {
            await Promise.all(
              missingUserIds.map(async (id) => {
                const participant = await fetchParticipant(id);
                userCache.set(id, participant);
              })
            );

            if (!isActive || currentSnapshotId !== latestSnapshotId) {
              return;
            }

            set({ conversations: buildConversations(docs), isLoading: false });
          })().catch((error) => {
            if (!isActive || currentSnapshotId !== latestSnapshotId) {
              return;
            }
            console.error('Error processing conversations:', error);
            set({ error: '会話の取得に失敗しました', isLoading: false });
          });
        },
        (error) => {
          console.error('Error subscribing to conversations:', error);
          set({ error: '会話の取得に失敗しました', isLoading: false });
        }
      );

      set({
        conversationsUnsubscribe: () => {
          isActive = false;
          unsubscribe();
        },
      });
    },

    // Unsubscribe from conversations
    unsubscribeFromConversations: () => {
      const unsubscribe = get().conversationsUnsubscribe;
      if (unsubscribe) {
        unsubscribe();
        set({ conversationsUnsubscribe: null });
      }
    },

    // Find existing conversation or create new one
    findOrCreateConversation: async (userId: string, otherUserId: string) => {
      try {
        set({ isLoading: true, error: null });

        const [senderState, recipientState] = await Promise.all([
          fetchUserAccessState(userId),
          fetchUserAccessState(otherUserId),
        ]);
        const restrictionReason = validateConversationRestriction(senderState, recipientState);
        if (restrictionReason) {
          throw new Error(restrictionReason);
        }

        // Search for existing conversation
        const conversationsRef = collection(db, 'conversations');
        const q = query(
          conversationsRef,
          where('participants', 'array-contains', userId)
        );
        const snapshot = await getDocs(q);

        // Find conversation with both users
        const existingConversation = snapshot.docs.find((docSnap) => {
          const data = docSnap.data() as { participants?: unknown };
          const participants = normalizeParticipants(data.participants);
          return participants.includes(otherUserId);
        });

        if (existingConversation) {
          return existingConversation.id;
        }

        // Create new conversation
        const newConversationRef = await addDoc(conversationsRef, {
          participants: [userId, otherUserId],
          unreadBy: {
            [userId]: 0,
            [otherUserId]: 0,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        return newConversationRef.id;
      } catch (error) {
        console.error('Error finding/creating conversation:', error);
        set({ error: error instanceof Error ? error.message : '会話の作成に失敗しました' });
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },

    // Get single conversation
    getConversation: async (conversationId: string, currentUserId: string) => {
      try {
        const conversationRef = doc(db, 'conversations', conversationId);
        const conversationSnap = await getDoc(conversationRef);

        if (!conversationSnap.exists()) {
          return null;
        }

        const data = conversationSnap.data() as Record<string, unknown>;
        const participants = normalizeParticipants(data.participants);
        const otherUserId = participants.find((id) => id !== currentUserId) || '';
        const participant = await fetchParticipant(otherUserId);

        return {
          id: conversationSnap.id,
          participants,
          lastMessage: typeof data.lastMessage === 'string' ? data.lastMessage : undefined,
          lastMessageAt: toDate(data.lastMessageAt),
          lastMessageBy: typeof data.lastMessageBy === 'string' ? data.lastMessageBy : undefined,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
          participant,
          unreadCount: getUnreadCount(data, currentUserId),
        };
      } catch (error) {
        console.error('Error getting conversation:', error);
        return null;
      }
    },

    // Subscribe to messages (real-time)
    subscribeToMessages: (conversationId: string) => {
      // Unsubscribe from previous subscription
      const prevUnsubscribe = get().messagesUnsubscribe;
      if (prevUnsubscribe) {
        prevUnsubscribe();
      }

      set({ isLoading: true, error: null, messages: [] });

      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      const q = query(
        messagesRef,
        orderBy('sentAt', 'asc'),
        limit(100)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const messagesData: Message[] = snapshot.docs.map((docSnap) => {
            const data = docSnap.data() as Record<string, unknown>;
            return {
              id: docSnap.id,
              senderId: typeof data.senderId === 'string' ? data.senderId : '',
              content: typeof data.content === 'string' ? data.content : '',
              type: data.type === 'image' ? 'image' : 'text',
              imageUrl: typeof data.imageUrl === 'string' ? data.imageUrl : undefined,
              sentAt: toDate(data.sentAt),
              readAt: data.readAt ? toDate(data.readAt) : undefined,
            };
          });

          set({ messages: messagesData, isLoading: false });
        },
        (error) => {
          console.error('Error subscribing to messages:', error);
          set({ error: 'メッセージの取得に失敗しました', isLoading: false });
        }
      );

      set({ messagesUnsubscribe: unsubscribe });
    },

    // Unsubscribe from messages
    unsubscribeFromMessages: () => {
      const unsubscribe = get().messagesUnsubscribe;
      if (unsubscribe) {
        unsubscribe();
        set({ messagesUnsubscribe: null, messages: [] });
      }
    },

    // Send text message
    sendMessage: async (conversationId: string, senderId: string, content: string, type: MessageType = 'text') => {
      const trimmedContent = content.trim();
      if (!trimmedContent) return;

      try {
        set({ isSending: true, error: null });

        const conversationRef = doc(db, 'conversations', conversationId);
        const messagesRef = collection(db, 'conversations', conversationId, 'messages');
        const messageRef = doc(messagesRef);
        const recipientIds = await resolveRecipientIds(conversationId, senderId);
        const senderState = await fetchUserAccessState(senderId);
        const recipientStates = await Promise.all(recipientIds.map((recipientId) => fetchUserAccessState(recipientId)));
        for (const recipientState of recipientStates) {
          const restrictionReason = validateConversationRestriction(senderState, recipientState);
          if (restrictionReason) {
            throw new Error(restrictionReason);
          }
        }

        const batch = writeBatch(db);
        batch.set(messageRef, {
          senderId,
          content: trimmedContent,
          type,
          sentAt: serverTimestamp(),
        });

        const conversationUpdate: Record<string, unknown> = {
          lastMessage: type === 'image' ? '📷 画像' : trimmedContent.substring(0, 100),
          lastMessageAt: serverTimestamp(),
          lastMessageBy: senderId,
          updatedAt: serverTimestamp(),
          [`unreadBy.${senderId}`]: 0,
        };
        recipientIds.forEach((recipientId) => {
          conversationUpdate[`unreadBy.${recipientId}`] = increment(1);
        });
        batch.update(conversationRef, conversationUpdate);

        await batch.commit();
      } catch (error) {
        console.error('Error sending message:', error);
        set({ error: error instanceof Error ? error.message : 'メッセージの送信に失敗しました' });
        throw error;
      } finally {
        set({ isSending: false });
      }
    },

    // Send image message
    sendImageMessage: async (conversationId: string, senderId: string, file: File) => {
      try {
        set({ isSending: true, error: null });

        // Upload image to Storage
        const timestamp = Date.now();
        const extension = file.name.split('.').pop() || 'jpg';
        const imagePath = `messages/${conversationId}/${timestamp}.${extension}`;
        const imageRef = ref(storage, imagePath);

        await uploadBytes(imageRef, file);
        const imageUrl = await getDownloadURL(imageRef);

        const conversationRef = doc(db, 'conversations', conversationId);
        const messagesRef = collection(db, 'conversations', conversationId, 'messages');
        const messageRef = doc(messagesRef);
        const recipientIds = await resolveRecipientIds(conversationId, senderId);
        const senderState = await fetchUserAccessState(senderId);
        const recipientStates = await Promise.all(recipientIds.map((recipientId) => fetchUserAccessState(recipientId)));
        for (const recipientState of recipientStates) {
          const restrictionReason = validateConversationRestriction(senderState, recipientState);
          if (restrictionReason) {
            throw new Error(restrictionReason);
          }
        }

        const batch = writeBatch(db);
        batch.set(messageRef, {
          senderId,
          content: '',
          type: 'image',
          imageUrl,
          sentAt: serverTimestamp(),
        });

        const conversationUpdate: Record<string, unknown> = {
          lastMessage: '📷 画像',
          lastMessageAt: serverTimestamp(),
          lastMessageBy: senderId,
          updatedAt: serverTimestamp(),
          [`unreadBy.${senderId}`]: 0,
        };
        recipientIds.forEach((recipientId) => {
          conversationUpdate[`unreadBy.${recipientId}`] = increment(1);
        });
        batch.update(conversationRef, conversationUpdate);

        await batch.commit();
      } catch (error) {
        console.error('Error sending image message:', error);
        set({ error: error instanceof Error ? error.message : '画像の送信に失敗しました' });
        throw error;
      } finally {
        set({ isSending: false });
      }
    },

    // Mark messages as read
    markAsRead: async (conversationId: string, messageIds: string[], readerId: string) => {
      const uniqueMessageIds = Array.from(new Set(messageIds));
      if (uniqueMessageIds.length === 0) return;

      const pendingKeys: string[] = [];
      const messageIdsToUpdate = uniqueMessageIds.filter((messageId) => {
        const key = `${conversationId}:${messageId}`;
        if (pendingReadMessageKeys.has(key)) {
          return false;
        }
        pendingReadMessageKeys.add(key);
        pendingKeys.push(key);
        return true;
      });

      if (messageIdsToUpdate.length === 0) return;

      try {
        for (let i = 0; i < messageIdsToUpdate.length; i += MAX_BATCH_WRITES) {
          const batch = writeBatch(db);
          const chunk = messageIdsToUpdate.slice(i, i + MAX_BATCH_WRITES);

          chunk.forEach((messageId) => {
            const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
            batch.update(messageRef, { readAt: serverTimestamp() });
          });

          if (i === 0) {
            const conversationRef = doc(db, 'conversations', conversationId);
            batch.update(conversationRef, {
              [`unreadBy.${readerId}`]: 0,
              updatedAt: serverTimestamp(),
            });
          }

          await batch.commit();
        }
      } catch (error) {
        console.error('Error marking messages as read:', error);
        // Don't throw - this is not critical
      } finally {
        pendingKeys.forEach((key) => pendingReadMessageKeys.delete(key));
      }
    },

    // Set current conversation
    setCurrentConversation: (conversation: ConversationWithDetails | null) => {
      set({ currentConversation: conversation });
    },

    clearError: () => {
      set({ error: null });
    },

    reset: () => {
      // Unsubscribe from all subscriptions
      const conversationsUnsubscribe = get().conversationsUnsubscribe;
      const messagesUnsubscribe = get().messagesUnsubscribe;

      if (conversationsUnsubscribe) conversationsUnsubscribe();
      if (messagesUnsubscribe) messagesUnsubscribe();

      set({
        conversations: [],
        messages: [],
        currentConversation: null,
        isLoading: false,
        isSending: false,
        error: null,
        conversationsUnsubscribe: null,
        messagesUnsubscribe: null,
      });
    },
  };
});
